/**
 * Shared date/time formatting helpers.
 *
 * All user-facing dates and timestamps MUST go through these helpers so the
 * output is consistent across the app and uses the Belgian locale (`fr-BE`)
 * regardless of the browser's locale setting.
 *
 * See CLAUDE.md → "Date and time formatting" for the project-wide rule.
 */

const LOCALE = 'fr-BE';

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DATE_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const DATE_NUMERIC_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DATE_TIME_SECONDS_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const DATE_TIME_NUMERIC_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Format an ISO 8601 string (or `null`) as a Belgian-style date-time:
 * e.g. `"14 juin 2024 04:33"`.
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not parseable as a date.
 */
export function formatDateTime(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    return value;
  }
  return DATE_TIME_FORMATTER.format(new Date(ts));
}

/**
 * Format an ISO 8601 string (or `null`) as a Belgian-style date-time **with seconds**:
 * e.g. `"14 juin 2024 04:33:37"`.
 *
 * Use this for a "last fetched" stamp next to a manual Refresh, where two reads within the same
 * minute must still be visibly distinct; use {@link formatDateTime} everywhere else.
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not parseable as a date.
 */
export function formatDateTimeWithSeconds(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    return value;
  }
  return DATE_TIME_SECONDS_FORMATTER.format(new Date(ts));
}

/**
 * Format an ISO 8601 string (or `null`) as a Belgian-style date (no time):
 * e.g. `"14 juin 2024"`.
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not parseable as a date.
 */
export function formatDate(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    return value;
  }
  return DATE_FORMATTER.format(new Date(ts));
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Local midnight of the calendar day containing `ts`. */
function startOfLocalDay(ts: number): number {
  const date = new Date(ts);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * Describe how far a date lies from now in **calendar days** (local time):
 * `"in 42 days"`, `"in 1 day"`, `"today"`, `"3 days ago"`. Meant for relative
 * hints rendered next to an absolute timestamp (e.g. `Valid to … (expires in
 * 42 days)`), so the caller supplies the verb. "today" means the same local
 * calendar day, and tomorrow is always "in 1 day" — not a ±24h window around
 * the current instant.
 *
 * - Returns `null` for `null` or unparseable input so callers can hide the
 *   hint entirely.
 */
export function formatRelativeDays(value: string | null, now: number = Date.now()): string | null {
  if (value === null) {
    return null;
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    return null;
  }
  // Rounding absorbs the 23h/25h days around DST transitions.
  const days = Math.round((startOfLocalDay(ts) - startOfLocalDay(now)) / DAY_MS);
  if (days === 0) {
    return 'today';
  }
  const unit = Math.abs(days) === 1 ? 'day' : 'days';
  return days > 0 ? `in ${days} ${unit}` : `${-days} ${unit} ago`;
}

/**
 * Format an ISO 8601 string (or `null`) as a numeric Belgian-style date:
 * e.g. `"14/06/2024"` (dd/mm/yyyy).
 *
 * Use this where a compact numeric date is wanted (e.g. list/detail tables),
 * as opposed to {@link formatDate}'s long-month form (`"14 juin 2024"`).
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not parseable as a date.
 */
export function formatDateNumeric(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    return value;
  }
  return DATE_NUMERIC_FORMATTER.format(new Date(ts));
}

const BARE_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Format a bare `YYYY-MM-DD` date string (or `null`) as a numeric Belgian-style
 * date: e.g. `"2026-08-19"` → `"19/08/2026"` (dd/mm/yyyy).
 *
 * Unlike {@link formatDateNumeric} this reformats **purely by string
 * manipulation** — bare dates fed to `Date.parse` are treated as UTC midnight,
 * which can render one calendar day off in local time. Use this for date-only
 * API fields that carry no timezone (e.g. a VM's `next_billing_at`).
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not a bare `YYYY-MM-DD` string.
 */
export function formatBareDateNumeric(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const match = BARE_DATE_RE.exec(value);
  if (match === null) {
    return value;
  }
  return `${match[3]}/${match[2]}/${match[1]}`;
}

const MONTH_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * Format a bare `YYYY-MM` or `YYYY-MM-DD` date string (or `null`) as a Belgian-style month + year:
 * e.g. `"2026-08-01"` → `"août 2026"`. Used for month-granularity fields such as an SPLA report's
 * `month`.
 *
 * The month/year are extracted **by string manipulation** and formatted on a fixed UTC mid-month
 * instant — a bare date fed to `Date.parse` is treated as UTC midnight, which a local-time
 * formatter can render one calendar month off for users west of UTC.
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not a `YYYY-MM[-DD]` string.
 */
export function formatMonthYear(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const match = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(value);
  if (match === null) {
    return value;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) {
    return value;
  }
  return MONTH_FORMATTER.format(new Date(Date.UTC(year, month - 1, 15)));
}

/**
 * Format a `YYYY-MM[-DD]` string (or `null`) as a numeric month/year: e.g. `"2028-12-31"` →
 * `"12/2028"`. Used for card expiry dates (`card_expiry` is the last day of the expiry month, and a
 * card is labelled by its month/year, never its day).
 *
 * Pure string manipulation — no `Date` object — so no timezone can shift the month.
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not a `YYYY-MM[-DD]` string with a real month.
 */
export function formatMonthYearNumeric(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const match = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(value);
  if (match === null) {
    return value;
  }
  const month = Number(match[2]);
  if (month < 1 || month > 12) {
    return value;
  }
  return `${match[2]}/${match[1]}`;
}

const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2})?$/;

/**
 * Format a `YYYY-MM-DD HH:MM:SS` **wall-clock** date-time string (or `null`) as a numeric
 * Belgian-style date-time: e.g. `"2026-07-25 03:32:00"` → `"25/07/2026 03:32"`.
 *
 * Like {@link formatBareDateNumeric} — and unlike {@link formatDateTimeNumeric} — this reformats
 * **purely by string manipulation, with no `Date` object at all**. That is the whole point: the
 * backup inventory (and any other endpoint documented as returning Europe/Brussels wall time)
 * sends offset-less, space-separated timestamps that the backend has *already* converted to the
 * application timezone. `Date.parse` treats such a string as **browser-local** time, so every
 * `Date`-based helper here would silently shift the value for a user outside Brussels — exactly
 * what the contract forbids. Displaying the digits verbatim is both correct and timezone-proof.
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not a `YYYY-MM-DD HH:MM[:SS]` string.
 */
export function formatWallClockDateTime(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const match = WALL_CLOCK_RE.exec(value);
  if (match === null) {
    return value;
  }
  return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
}

// Today's calendar date in the application timezone, as a UTC-midnight epoch — the comparable
// counterpart of a wall-clock string's own calendar date. en-CA is used purely for its YYYY-MM-DD
// output shape.
const BRUSSELS_DAY_FORMATTER = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels' });

/**
 * Describe how far a **wall-clock** `YYYY-MM-DD HH:MM[:SS]` timestamp lies from now, in calendar
 * days: `"today"`, `"in 2 days"`, `"3 days ago"`. The wall-clock counterpart of
 * {@link formatRelativeDays}: the input carries no timezone and is already Europe/Brussels wall
 * time, so the calendar date is extracted **by string manipulation** (feeding it to `Date.parse`
 * would read it as browser-local time and drift a day for users outside Brussels) and compared
 * against today's Brussels calendar date.
 *
 * - Returns `null` for `null` or non-wall-clock input so callers can hide the hint entirely.
 */
export function formatRelativeDaysWallClock(value: string | null, now: number = Date.now()): string | null {
  if (value === null) {
    return null;
  }
  const match = WALL_CLOCK_RE.exec(value);
  if (match === null) {
    return null;
  }
  const target = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const [todayYear, todayMonth, todayDay] = BRUSSELS_DAY_FORMATTER.format(now).split('-').map(Number);
  const today = Date.UTC(todayYear, todayMonth - 1, todayDay);
  const days = Math.round((target - today) / DAY_MS);
  if (days === 0) {
    return 'today';
  }
  const unit = Math.abs(days) === 1 ? 'day' : 'days';
  return days > 0 ? `in ${days} ${unit}` : `${-days} ${unit} ago`;
}

/**
 * Format an ISO 8601 string (or `null`) as a compact numeric Belgian-style date-time:
 * e.g. `"14/06/2024 04:33"` (dd/mm/yyyy HH:mm, 24-hour).
 *
 * Use this where a short date **and** time are wanted in tight space (e.g. an inline badge or a
 * table cell), as opposed to {@link formatDateTime}'s long-month form (`"14 juin 2024 04:33"`).
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not parseable as a date.
 */
export function formatDateTimeNumeric(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    return value;
  }
  return DATE_TIME_NUMERIC_FORMATTER.format(new Date(ts));
}

const BRUSSELS_DATE_TIME_NUMERIC_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/Brussels',
});

/**
 * Format an ISO 8601 **instant** (or `null`) as a compact numeric date-time **in Europe/Brussels**,
 * whatever the browser's timezone: e.g. `"2026-09-28T16:58:51+00:00"` → `"28/09/2026 18:58"`.
 *
 * Use this where the contract says an instant must be read in Brussels time (the accountant's
 * reconciliation views book every movement on a Brussels calendar day); {@link formatDateTimeNumeric}
 * renders in the browser's own timezone instead.
 *
 * - Returns `'—'` (em dash) for `null`.
 * - Returns the input unchanged if it is not parseable as a date.
 */
export function formatInstantBrussels(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) {
    return value;
  }
  return BRUSSELS_DATE_TIME_NUMERIC_FORMATTER.format(new Date(ts));
}

/** Today's calendar date in Europe/Brussels as `YYYY-MM-DD` (e.g. to pick a default month or year). */
export function brusselsToday(now: number = Date.now()): string {
  return BRUSSELS_DAY_FORMATTER.format(now);
}
